/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Modal from '../../../../components/Modal/Modal';
import { PrimaryButton, GhostButton, SmallButton } from '../../../../styles/ui/Button';
import { FormGrid } from '../../../../styles/layout/FormGrid';
import { Field, RequiredMark } from '../../../../styles/forms/Field';
import {
   createCredential,
   rotateCredential,
} from '../../../../services/finance/integrationCredentialsApi';
import { readApiError, financeErrorMessage } from '../../utils/apiError';
import { ENVIRONMENT_OPTIONS, DEFAULT_PROVIDER } from '../../constants';

// ---------------------------------------------------------------------------
// Cadastro e ROTAÇÃO da chave de API (handoff 0079 §3.2 e §3.3).
//
// ⚠️ O campo é WRITE-ONLY: nasce vazio nos dois modos e nunca é pré-preenchido —
// nem no modo rotação, porque nenhuma resposta da API devolve a chave. Não
// existe "editar a chave": existe rotacionar.
//
// A chave vive só neste estado local, some ao fechar, e não vai para estado
// global/localStorage/log. Por isso o input também desliga autocomplete e
// corretor: nada de o navegador guardar segredo em formulário salvo.
// ---------------------------------------------------------------------------

const FieldError = styled.span`
   font-size: 11px;
   color: ${({ theme }) => theme?.colors?.danger ?? '#b71c1c'};
   margin-top: 2px;
`;

const Hint = styled.p`
   margin: 0 0 14px;
   font-size: 12.5px;
   line-height: 1.5;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#555'};
`;

const KeyRow = styled.div`
   display: flex;
   gap: 8px;
   align-items: flex-start;

   input {
      flex: 1;
   }
`;

export default function CredentialFormModal({
   isOpen,
   onClose,
   mode,
   bankAccountId,
   credential,
   onSaved,
}) {
   const isRotate = mode === 'rotate';

   const [environment, setEnvironment] = useState('sandbox');
   const [apiKey, setApiKey] = useState('');
   const [showKey, setShowKey] = useState(false);
   const [fieldError, setFieldError] = useState(null);
   const [isSubmitting, setIsSubmitting] = useState(false);

   // Reset a cada abertura — inclusive a chave, que nunca persiste entre usos.
   useEffect(() => {
      if (isOpen) {
         setEnvironment(credential?.environment ?? 'sandbox');
         setApiKey('');
         setShowKey(false);
         setFieldError(null);
         setIsSubmitting(false);
      }
   }, [isOpen, credential]);

   const handleConfirm = async () => {
      if (isSubmitting) return;

      // O backend faz trim; validamos sobre o valor já aparado para não aceitar
      // uma chave que é só espaço.
      const trimmed = apiKey.trim();
      if (trimmed.length < 10) {
         setFieldError('Informe a chave de API (mínimo de 10 caracteres).');
         return;
      }

      setIsSubmitting(true);
      setFieldError(null);
      try {
         if (isRotate) {
            const { message } = await rotateCredential(credential.id, trimmed);
            toast.success(message);
         } else {
            const { message } = await createCredential(bankAccountId, {
               provider: DEFAULT_PROVIDER,
               environment,
               apiKey: trimmed,
            });
            toast.success(message);
         }
         setApiKey('');
         onSaved?.();
         onClose?.();
      } catch (err) {
         const { fieldErrors } = readApiError(err);
         if (fieldErrors.api_key) {
            setFieldError(fieldErrors.api_key);
         } else {
            toast.error(
               financeErrorMessage(
                  err,
                  isRotate
                     ? 'Erro ao rotacionar a chave.'
                     : 'Erro ao cadastrar a credencial.',
               ),
            );
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   const footer = (
      <>
         <GhostButton type="button" onClick={onClose} disabled={isSubmitting}>
            Cancelar
         </GhostButton>
         <PrimaryButton
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
         >
            {/* eslint-disable-next-line no-nested-ternary */}
            {isSubmitting
               ? 'Salvando…'
               : isRotate
                 ? 'Rotacionar chave'
                 : 'Cadastrar credencial'}
         </PrimaryButton>
      </>
   );

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title={isRotate ? 'Rotacionar chave de API' : 'Nova credencial'}
         maxWidth="560px"
         footer={footer}
      >
         <Hint>
            {isRotate
               ? 'Gere uma chave nova no painel da ASAAS e cole aqui. A chave atual deixa de ser usada assim que a troca for salva — vale testar a conexão logo depois.'
               : 'Cole a chave de API gerada no painel da ASAAS. Ela é cifrada no servidor e nunca mais é exibida: daqui em diante você verá apenas os primeiros e últimos caracteres.'}
         </Hint>

         <FormGrid>
            {!isRotate && (
               <Field className="col-4">
                  <label htmlFor="credential-environment">
                     Ambiente
                     <RequiredMark>*</RequiredMark>
                  </label>
                  <select
                     id="credential-environment"
                     value={environment}
                     onChange={(e) => setEnvironment(e.target.value)}
                     disabled={isSubmitting}
                  >
                     {ENVIRONMENT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                           {option.label}
                        </option>
                     ))}
                  </select>
               </Field>
            )}

            <Field className={isRotate ? 'col-12' : 'col-8'}>
               <label htmlFor="credential-api-key">
                  Chave de API
                  <RequiredMark>*</RequiredMark>
               </label>
               <KeyRow>
                  <input
                     id="credential-api-key"
                     type={showKey ? 'text' : 'password'}
                     value={apiKey}
                     onChange={(e) => {
                        setApiKey(e.target.value);
                        setFieldError(null);
                     }}
                     placeholder="$aact_..."
                     maxLength={500}
                     disabled={isSubmitting}
                     autoComplete="off"
                     spellCheck={false}
                     aria-invalid={!!fieldError || undefined}
                  />
                  <SmallButton
                     type="button"
                     onClick={() => setShowKey((prev) => !prev)}
                     disabled={isSubmitting}
                  >
                     {showKey ? 'Ocultar' : 'Mostrar'}
                  </SmallButton>
               </KeyRow>
               {fieldError && <FieldError>{fieldError}</FieldError>}
            </Field>
         </FormGrid>
      </Modal>
   );
}

CredentialFormModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   mode: PropTypes.oneOf(['create', 'rotate']).isRequired,
   bankAccountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   credential: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      environment: PropTypes.string,
   }),
   onSaved: PropTypes.func,
};
